/**
 * TELA 7 — LEADS
 *
 * Quem respondeu. A tela responde três perguntas nesta ordem: quem é, **de
 * onde veio** e quem atende.
 *
 * A do meio é a que dá trabalho. O WhatsApp entrega LID no lugar do telefone,
 * e sem tratamento a resposta do lead não liga na campanha que a gerou — por
 * isso a atribuição é exibida com a **camada** que a resolveu e a **confiança**
 * do resultado, não como um fato liso.
 *
 * E a regra que justifica a coluna inteira: `origem desconhecida` é
 * **contada, nunca rateada** entre as campanhas. Distribuir proporcionalmente
 * o que não se sabe é como somar "conflito de sessão" com "ban" — a série
 * inteira deixa de servir para decidir.
 */
import { useCallback, useMemo, useState } from "react";
import type { Lead, UsuarioResumo } from "@co/contracts";
import { api, type RespostaLeads } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  Selo,
  Tabela,
  classeEntrada,
  type Tom,
} from "../ui/base.js";
import {
  Conteudo,
  Indicadores,
  LinhaVazia,
  Td,
  fmtDataHora,
  fmtNum,
  pctDe,
  useCarga,
} from "../ui/comercial.js";

type EstadoLead = Lead["estado"];
type Confianca = Lead["atribuicao"]["confianca"];

const TOM_ESTADO: Record<EstadoLead, Tom> = {
  novo: "ember",
  atribuido: "cool",
  em_contato: "marca",
  convertido: "ok",
  perdido: "idle",
};

const ROTULO_ESTADO: Record<EstadoLead, string> = {
  novo: "novo",
  atribuido: "atribuído",
  em_contato: "em contato",
  convertido: "convertido",
  perdido: "perdido",
};

const TOM_CONFIANCA: Record<Confianca, Tom> = {
  certa: "ok",
  provavel: "warn",
  desconhecida: "idle",
};

/**
 * O que cada nível significa. Escrito aqui, na tela, porque o contrato
 * entrega o enum e a camada, mas nenhum texto — e um selo cinza sem
 * explicação é exatamente o tipo de sinal que o operador aprende a ignorar.
 */
const EXPLICA_CONFIANCA: Record<Confianca, string> = {
  certa:
    "o retorno traz o identificador do envio: dá para apontar campanha, chip e variação sem inferência",
  provavel:
    "a ligação foi reconstruída por janela de tempo e pelo chip que falou com o contato; é a melhor hipótese, não um fato",
  desconhecida:
    "não há como ligar esta resposta a uma campanha; fica contada à parte e nunca é rateada entre as campanhas",
};

/**
 * Só os estados que o servidor produz HOJE.
 *
 * A lista trazia também "Em contato", "Convertidos" e "Perdidos". O servidor
 * deriva o estado de uma coisa só — `responsavel_id ? "atribuido" : "novo"` —
 * então aquelas três opções nunca poderiam devolver nada. Filtro que sempre
 * volta vazio faz o operador concluir que não há lead naquele estágio, quando
 * na verdade o estágio não existe. Voltam quando o CRM (módulo 4) os produzir.
 */
const ESTADOS: ReadonlyArray<{ id: "" | EstadoLead; nome: string }> = [
  { id: "", nome: "Todos os estados" },
  { id: "novo", nome: "Sem responsável" },
  { id: "atribuido", nome: "Já atribuídos" },
];

const CONFIANCAS: ReadonlyArray<{ id: "" | Confianca; nome: string }> = [
  { id: "", nome: "Qualquer confiança" },
  { id: "certa", nome: "Atribuição certa" },
  { id: "provavel", nome: "Atribuição provável" },
  { id: "desconhecida", nome: "Origem desconhecida" },
];

const LIMITE = 50;

interface Dados {
  leads: RespostaLeads;
  /** Pode faltar: consultor não enxerga a lista de usuários. */
  usuarios: UsuarioResumo[] | null;
}

export function Leads(): JSX.Element {
  const [estadoFiltro, setEstadoFiltro] = useState<"" | EstadoLead>("");
  const [desloc, setDesloc] = useState(0);

  // Filtros que o servidor entende vão na query; os demais são aplicados
  // sobre a página carregada e rotulados como tal — não vale sugerir que
  // um recorte local é o recorte da base inteira.
  const buscar = useCallback(async (): Promise<Dados> => {
    const filtro: Record<string, string | number | undefined> = {
      limite: LIMITE,
      desloc,
    };
    if (estadoFiltro !== "") filtro["estado"] = estadoFiltro;
    const [leads, usuarios] = await Promise.all([
      api.leads(filtro),
      api.usuarios().catch((): null => null),
    ]);
    return { leads, usuarios: usuarios ? usuarios.itens : null };
  }, [estadoFiltro, desloc]);

  const { estado, recarregar } = useCarga(buscar);

  return (
    <div className="grid gap-4">
      <Conteudo estado={estado} aoRecarregar={recarregar}>
        {(d) => (
          <CorpoDeLeads
            dados={d}
            estadoFiltro={estadoFiltro}
            aoTrocarEstado={(v) => {
              setEstadoFiltro(v);
              setDesloc(0);
            }}
            desloc={desloc}
            aoDeslocar={setDesloc}
            aoRecarregar={recarregar}
          />
        )}
      </Conteudo>
    </div>
  );
}

function CorpoDeLeads({
  dados,
  estadoFiltro,
  aoTrocarEstado,
  desloc,
  aoDeslocar,
  aoRecarregar,
}: {
  dados: Dados;
  estadoFiltro: "" | EstadoLead;
  aoTrocarEstado: (v: "" | EstadoLead) => void;
  desloc: number;
  aoDeslocar: (v: number) => void;
  aoRecarregar: () => void;
}): JSX.Element {
  const [confianca, setConfianca] = useState<"" | Confianca>("");
  const [busca, setBusca] = useState("");
  const [atribuindo, setAtribuindo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const { itens, pagina, resumo } = dados.leads;

  const desconhecidos = itens.filter(
    (l) => l.atribuicao.confianca === "desconhecida",
  ).length;

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens.filter((l) => {
      if (confianca !== "" && l.atribuicao.confianca !== confianca) return false;
      if (!termo) return true;
      return (
        (l.contatoNome ?? "").toLowerCase().includes(termo) ||
        l.telefoneExibicao.toLowerCase().includes(termo) ||
        (l.atribuicao.campanhaNome ?? "").toLowerCase().includes(termo)
      );
    });
  }, [itens, confianca, busca]);

  /** Vendedor é quem atende a carteira; supervisor também assume lead. */
  const vendedores = useMemo(
    () =>
      (dados.usuarios ?? [])
        .filter((u) => u.ativo && u.perfil !== "administrador")
        .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR")),
    [dados.usuarios],
  );

  async function atribuir(leadId: string, responsavelId: string): Promise<void> {
    if (!responsavelId) return;
    setAtribuindo(leadId);
    setErro(null);
    try {
      await api.atribuirLead(leadId, responsavelId);
      aoRecarregar();
    } catch (e: unknown) {
      setErro(
        e instanceof Error ? e.message : "Não foi possível atribuir o lead.",
      );
    } finally {
      setAtribuindo(null);
    }
  }

  return (
    <div className="grid gap-4">
      <Indicadores>
        <Indicador
          rotulo="Novos"
          valor={fmtNum(resumo.novos)}
          nota="ainda sem dono"
          tom="ember"
        />
        <Indicador
          rotulo="Atribuídos"
          valor={fmtNum(resumo.atribuidos)}
          nota="já têm vendedor"
        />
        <Indicador
          rotulo="Convertidos"
          valor={fmtNum(resumo.convertidos)}
          nota={`${pctDe(resumo.convertidos, pagina.total)}% do total`}
          tom="ok"
        />
        <Indicador
          rotulo="Origem desconhecida"
          valor={fmtNum(desconhecidos)}
          nota={`de ${itens.length} nesta página · nunca rateados`}
        />
      </Indicadores>

      {desconhecidos > 0 && (
        <Aviso tom="warn">
          <strong>
            {desconhecidos} resposta(s) sem campanha identificada nesta página.
          </strong>{" "}
          Elas ficam contadas à parte e não são distribuídas entre as campanhas.
          Ratear o desconhecido inflaria a taxa de resposta de quem não a
          produziu, e é justamente essa taxa que decide se o disparo continua.
        </Aviso>
      )}

      {erro && <Aviso tom="crit">{erro}</Aviso>}

      {dados.usuarios === null && (
        <Aviso tom="cool">
          A lista de usuários não está disponível para o seu perfil, então a
          atribuição a um vendedor fica desabilitada aqui. Peça a um gerente ou
          administrador.
        </Aviso>
      )}

      <Painel
        titulo="Leads"
        dica={`${fmtNum(pagina.total)} no total · ${visiveis.length} exibidos`}
        semCorpo
      >
        <div className="p-5 border-b border-linha grid gap-3 sm:grid-cols-3">
          <Campo rotulo="Estado" dica="Filtro aplicado pelo servidor">
            <select
              className={classeEntrada}
              value={estadoFiltro}
              onChange={(e) =>
                aoTrocarEstado(e.target.value as "" | EstadoLead)
              }
            >
              {ESTADOS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Confiança da atribuição"
            dica="Filtro aplicado sobre esta página"
          >
            <select
              className={classeEntrada}
              value={confianca}
              onChange={(e) => setConfianca(e.target.value as "" | Confianca)}
            >
              {CONFIANCAS.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.nome}
                </option>
              ))}
            </select>
          </Campo>
          <Campo rotulo="Buscar" dica="Nome, telefone ou campanha, nesta página">
            <input
              type="search"
              className={classeEntrada}
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Ex.: padaria"
            />
          </Campo>
        </div>

        <Tabela
          larguraMin={1040}
          colunas={[
            { chave: "contato", rotulo: "Contato" },
            { chave: "chegou", rotulo: "Chegou" },
            { chave: "origem", rotulo: "De onde veio" },
            { chave: "msg", rotulo: "Primeira mensagem" },
            { chave: "estado", rotulo: "Estado" },
            { chave: "dono", rotulo: "Responsável" },
          ]}
        >
          {visiveis.length === 0 ? (
            <LinhaVazia colunas={6}>
              {itens.length === 0
                ? "Nenhum lead ainda. Eles nascem das respostas às campanhas."
                : "Nenhum lead desta página passa pelos filtros locais."}
            </LinhaVazia>
          ) : (
            visiveis.map((l) => (
              <tr key={l.id}>
                <Td>
                  <div className="font-medium">{l.contatoNome ?? "sem nome"}</div>
                  <div className="font-mono text-[11.5px] text-tinta-3 mt-0.5">
                    {l.telefoneExibicao}
                  </div>
                </Td>
                <Td className="whitespace-nowrap text-[11.5px] text-tinta-3">
                  {fmtDataHora(l.chegouEm)}
                </Td>
                <Td className="min-w-[240px]">
                  <Atribuicao atribuicao={l.atribuicao} />
                </Td>
                <Td className="max-w-[36ch]">
                  <p className="text-[12.5px] text-tinta-2 leading-relaxed line-clamp-3 whitespace-pre-wrap">
                    {l.primeiraMensagem ?? "—"}
                  </p>
                </Td>
                <Td>
                  <Selo tom={TOM_ESTADO[l.estado]}>
                    {ROTULO_ESTADO[l.estado]}
                  </Selo>
                </Td>
                <Td className="min-w-[190px]">
                  <div className="text-[12.5px] text-tinta mb-1.5">
                    {l.responsavelNome ?? "sem dono"}
                  </div>
                  {vendedores.length > 0 && (
                    <select
                      aria-label={`Atribuir ${l.contatoNome ?? l.telefoneExibicao} a um vendedor`}
                      className={classeEntrada}
                      value=""
                      disabled={atribuindo === l.id}
                      onChange={(e) => void atribuir(l.id, e.target.value)}
                    >
                      <option value="">
                        {l.responsavelNome ? "Transferir para…" : "Atribuir a…"}
                      </option>
                      {vendedores.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome} — {u.perfil}
                        </option>
                      ))}
                    </select>
                  )}
                </Td>
              </tr>
            ))
          )}
        </Tabela>

        <div className="px-5 py-3 border-t border-linha flex items-center gap-3 flex-wrap">
          <span className="text-[11.5px] text-tinta-3 tabular">
            {pagina.total === 0
              ? "nenhum registro"
              : `${desloc + 1}–${Math.min(desloc + itens.length, pagina.total)} de ${fmtNum(pagina.total)}`}
          </span>
          <div className="ml-auto flex gap-2">
            <Botao
              pequeno
              desabilitado={desloc === 0}
              onClick={() => aoDeslocar(Math.max(0, desloc - LIMITE))}
            >
              Anteriores
            </Botao>
            <Botao
              pequeno
              desabilitado={desloc + itens.length >= pagina.total}
              onClick={() => aoDeslocar(desloc + LIMITE)}
            >
              Próximos
            </Botao>
          </div>
        </div>
      </Painel>

      <Painel
        titulo="Como a origem é resolvida"
        dica="A confiança é parte do dado, não um detalhe de implementação"
      >
        <ul className="grid gap-3">
          {(["certa", "provavel", "desconhecida"] as const).map((c) => (
            <li key={c} className="flex gap-3 items-baseline">
              <span className="shrink-0">
                <Selo tom={TOM_CONFIANCA[c]}>{c}</Selo>
              </span>
              <span className="text-[12.5px] text-tinta-2 leading-relaxed">
                {EXPLICA_CONFIANCA[c]}
              </span>
            </li>
          ))}
        </ul>
      </Painel>
    </div>
  );
}

/** De qual campanha, chip e variação veio — e com que confiança. */
function Atribuicao({
  atribuicao,
}: {
  atribuicao: Lead["atribuicao"];
}): JSX.Element {
  const a = atribuicao;
  if (a.confianca === "desconhecida") {
    return (
      <div>
        <Selo tom="idle" titulo={EXPLICA_CONFIANCA.desconhecida}>
          origem desconhecida
        </Selo>
        <p className="text-[11.5px] text-tinta-3 mt-1 leading-relaxed">
          Contada à parte, nunca rateada entre campanhas.
        </p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-[12.5px] text-tinta">
        {a.campanhaNome ?? "campanha não identificada"}
      </div>
      <div className="text-[11.5px] text-tinta-3 mt-0.5">
        chip {a.numeroExibicao ?? "—"}
        {a.variacaoOrdem !== null ? ` · variação ${a.variacaoOrdem}` : ""}
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        <Selo
          tom={TOM_CONFIANCA[a.confianca]}
          titulo={EXPLICA_CONFIANCA[a.confianca]}
        >
          {a.confianca}
        </Selo>
        <span className="text-[11px] text-tinta-3">
          resolvida na camada {a.camada}
        </span>
      </div>
    </div>
  );
}
